import { useDeferredValue, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, CalendarClock, Check, Mail, Pause, Play, Search, Send, Sparkles, UsersRound } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../lib_api';

type AudienceMember = {
  id: number;
  email: string;
  fullName: string;
  planName?: string | null;
  promotionalEmailEligible: boolean;
  optedOutAt?: string | null;
};

type Campaign = {
  id: number;
  name: string;
  subject: string;
  scheduleType: 'once' | 'weekly' | 'monthly';
  status: string;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  createdAt: string;
  recipientCount: number;
  queuedCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
};

const emptyComposer = {
  name: '',
  subject: '',
  previewText: '',
  bodyText: '',
  ctaLabel: 'Open Pickaxe',
  ctaUrl: 'https://pickaxetrader.com',
};

function dateTimeLabel(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

function statusTone(status: string) {
  if (status === 'sent' || status === 'active') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200';
  if (status === 'failed' || status === 'cancelled') return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-200';
  if (status === 'paused') return 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-100';
  return 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-100';
}

export default function EmailCampaigns() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'compose' | 'history'>('compose');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [composer, setComposer] = useState(emptyComposer);
  const [goal, setGoal] = useState('');
  const [tone, setTone] = useState('Clear and professional');
  const [offer, setOffer] = useState('');
  const [scheduleType, setScheduleType] = useState<'once' | 'weekly' | 'monthly'>('once');
  const [scheduledAt, setScheduledAt] = useState('');
  const goalInput = useRef<HTMLTextAreaElement>(null);
  const deferredSearch = useDeferredValue(search.trim());
  const goalReady = goal.trim().length >= 8;

  const audience = useQuery<{ items: AudienceMember[] }>({
    queryKey: ['admin', 'email-campaign-audience', deferredSearch, plan],
    queryFn: async () => (await api.get('/api/admin/email-campaigns/audience', {
      params: { search: deferredSearch, plan: plan === 'all' ? '' : plan, limit: 300 },
    })).data,
  });
  const campaigns = useQuery<{ items: Campaign[] }>({
    queryKey: ['admin', 'email-campaigns'],
    queryFn: async () => (await api.get('/api/admin/email-campaigns')).data,
    refetchInterval: 30_000,
  });

  const visibleEligible = useMemo(
    () => (audience.data?.items || []).filter((member) => member.promotionalEmailEligible),
    [audience.data],
  );
  const allVisibleSelected = visibleEligible.length > 0 && visibleEligible.every((member) => selected.has(member.id));

  const aiDraft = useMutation({
    mutationFn: async () => (await api.post('/api/admin/email-campaigns/ai-draft', {
      goal,
      audience: plan === 'all' ? 'registered Pickaxe users' : `${plan} users`,
      tone,
      offer,
      ctaUrl: composer.ctaUrl,
    })).data,
    onSuccess: (draft) => {
      setComposer({
        name: draft.campaignName,
        subject: draft.subject,
        previewText: draft.previewText,
        bodyText: draft.bodyText,
        ctaLabel: draft.ctaLabel,
        ctaUrl: draft.ctaUrl,
      });
      toast.success('AI draft is ready for review.');
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Unable to generate the email draft.'),
  });

  const saveCampaign = useMutation({
    mutationFn: async (action: 'draft' | 'send' | 'schedule') => (await api.post('/api/admin/email-campaigns', {
      ...composer,
      action,
      scheduleType,
      scheduledAt: action === 'schedule' ? new Date(scheduledAt).toISOString() : null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      recipientIds: [...selected],
    })).data,
    onSuccess: (_data, action) => {
      toast.success(action === 'draft' ? 'Campaign draft saved.' : action === 'send' ? 'Campaign queued safely.' : 'Campaign scheduled.');
      setComposer(emptyComposer);
      setGoal('');
      setOffer('');
      setSelected(new Set());
      setScheduledAt('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-campaigns'] });
      setTab('history');
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Unable to save the campaign.'),
  });

  const pauseCampaign = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'pause' | 'resume' }) => (await api.post(`/api/admin/email-campaigns/${id}/${action}`)).data,
    onSuccess: (_data, variables) => {
      toast.success(variables.action === 'pause' ? 'Campaign paused.' : 'Campaign resumed.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-campaigns'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Unable to pause campaign.'),
  });

  function toggleAllVisible() {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleEligible.forEach((member) => next.delete(member.id));
      else visibleEligible.forEach((member) => next.add(member.id));
      return next;
    });
  }

  function submit(action: 'draft' | 'send' | 'schedule') {
    if (!selected.size) return toast.error('Select at least one eligible recipient.');
    if (!composer.name || !composer.subject || !composer.bodyText) return toast.error('Campaign name, subject, and message are required.');
    if (action === 'schedule' && !scheduledAt) return toast.error('Choose the first delivery time.');
    if (action === 'send' && !window.confirm(`Queue this promotion for ${selected.size} recipient${selected.size === 1 ? '' : 's'}?`)) return;
    saveCampaign.mutate(action);
  }

  function generateDraft() {
    if (!goalReady) {
      toast.info('Describe the campaign goal using at least 8 characters.');
      goalInput.current?.focus();
      return;
    }
    aiDraft.mutate();
  }

  return (
    <div className="space-y-4">
      <section className="panel flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200"><Mail className="h-5 w-5" /></span>
          <div><h3 className="font-semibold">Promotional email desk</h3><p className="text-xs text-mutedGray dark:text-darkTextSecondary">AI-assisted drafting with controlled, consent-aware delivery.</p></div>
        </div>
        <div className="flex rounded-lg border border-gray-200 p-1 dark:border-darkCard">
          <button className={`rounded-md px-3 py-2 text-sm ${tab === 'compose' ? 'bg-indigo-600 text-white' : ''}`} onClick={() => setTab('compose')}>Compose</button>
          <button className={`rounded-md px-3 py-2 text-sm ${tab === 'history' ? 'bg-indigo-600 text-white' : ''}`} onClick={() => setTab('history')}>Campaigns</button>
        </div>
      </section>

      {tab === 'compose' ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.72fr)]">
          <div className="space-y-4">
            <section className="panel overflow-hidden">
              <div className="border-b border-gray-200 p-4 dark:border-darkCard">
                <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-500" /><h3 className="font-semibold">Create with AI</h3></div>
                <p className="mt-1 text-xs text-mutedGray dark:text-darkTextSecondary">Describe the purpose. AI prepares editable copy without profit guarantees.</p>
              </div>
              <div className="grid gap-3 p-4 md:grid-cols-2">
                <label className="md:col-span-2"><span className="mb-1 flex items-center justify-between text-xs font-medium"><span>Campaign goal <strong className="text-red-500">Required</strong></span><span className={goalReady ? 'text-emerald-600' : 'text-mutedGray dark:text-darkTextSecondary'}>{goal.trim().length}/8 minimum</span></span><textarea ref={goalInput} className="input min-h-24" placeholder="Example: Invite trial users to explore the 12-week Platinum offer and Trade AutoPilot." value={goal} onChange={(event) => setGoal(event.target.value)} /></label>
                <label><span className="mb-1 block text-xs font-medium">Tone</span><select className="input" value={tone} onChange={(event) => setTone(event.target.value)}><option>Clear and professional</option><option>Warm and encouraging</option><option>Short and urgent</option><option>Educational</option></select></label>
                <label><span className="mb-1 block text-xs font-medium">Offer or key detail</span><input className="input" placeholder="Optional offer, deadline, or benefit" value={offer} onChange={(event) => setOffer(event.target.value)} /></label>
                <div className="md:col-span-2"><button className="btn w-full disabled:cursor-wait disabled:opacity-60" disabled={aiDraft.isPending} onClick={generateDraft}><Bot className="h-4 w-4" />{aiDraft.isPending ? 'AI is preparing your draft…' : 'Generate editable draft'}</button>{!goalReady ? <p className="mt-2 text-center text-xs text-amber-700 dark:text-amber-200">Enter the campaign goal above, then generate the draft.</p> : null}</div>
              </div>
            </section>

            <section className="panel overflow-hidden">
              <div className="border-b border-gray-200 p-4 dark:border-darkCard"><h3 className="font-semibold">Message</h3></div>
              <div className="grid gap-3 p-4">
                <label><span className="mb-1 block text-xs font-medium">Internal campaign name</span><input className="input" value={composer.name} onChange={(event) => setComposer({ ...composer, name: event.target.value })} /></label>
                <label><span className="mb-1 block text-xs font-medium">Subject</span><input className="input" maxLength={160} value={composer.subject} onChange={(event) => setComposer({ ...composer, subject: event.target.value })} /></label>
                <label><span className="mb-1 block text-xs font-medium">Inbox preview</span><input className="input" maxLength={240} value={composer.previewText} onChange={(event) => setComposer({ ...composer, previewText: event.target.value })} /></label>
                <label><span className="mb-1 block text-xs font-medium">Email message</span><textarea className="input min-h-48 leading-6" value={composer.bodyText} onChange={(event) => setComposer({ ...composer, bodyText: event.target.value })} /></label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label><span className="mb-1 block text-xs font-medium">Button label</span><input className="input" value={composer.ctaLabel} onChange={(event) => setComposer({ ...composer, ctaLabel: event.target.value })} /></label>
                  <label><span className="mb-1 block text-xs font-medium">Button destination</span><input className="input" type="url" value={composer.ctaUrl} onChange={(event) => setComposer({ ...composer, ctaUrl: event.target.value })} /></label>
                </div>
              </div>
            </section>

            <section className="panel overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-darkCard">
                <div><div className="flex items-center gap-2"><UsersRound className="h-4 w-4 text-indigo-500" /><h3 className="font-semibold">Recipients</h3></div><p className="mt-1 text-xs text-mutedGray dark:text-darkTextSecondary">{selected.size} selected. Unsubscribed users cannot be added.</p></div>
                <div className="flex w-full gap-2 sm:w-auto"><label className="relative flex-1 sm:w-64"><Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" /><input className="input pl-9" placeholder="Search users" value={search} onChange={(event) => setSearch(event.target.value)} /></label><select className="input w-32" value={plan} onChange={(event) => setPlan(event.target.value)}><option value="all">All plans</option><option value="gold">Gold</option><option value="platinum">Platinum</option><option value="none">No plan</option></select></div>
              </div>
              <div className="max-h-96 overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-left dark:bg-[#172534]"><tr><th className="w-12 px-4 py-3"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} aria-label="Select all eligible users shown" /></th><th className="px-3 py-3">User</th><th className="px-3 py-3">Access</th><th className="px-3 py-3">Email status</th></tr></thead>
                  <tbody>{(audience.data?.items || []).map((member) => <tr key={member.id} className="border-t border-gray-100 dark:border-darkCard"><td className="px-4 py-3"><input type="checkbox" disabled={!member.promotionalEmailEligible} checked={selected.has(member.id)} onChange={() => setSelected((current) => { const next = new Set(current); if (next.has(member.id)) next.delete(member.id); else next.add(member.id); return next; })} aria-label={`Select ${member.email}`} /></td><td className="px-3 py-3"><strong className="block">{member.fullName || 'Unnamed user'}</strong><span className="text-xs text-mutedGray dark:text-darkTextSecondary">{member.email}</span></td><td className="px-3 py-3">{member.planName || 'No active plan'}</td><td className="px-3 py-3">{member.promotionalEmailEligible ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300"><Check className="h-3.5 w-3.5" />Eligible</span> : <span className="text-amber-700 dark:text-amber-200">Unsubscribed</span>}</td></tr>)}</tbody>
                </table>
                {audience.isLoading ? <p className="p-5 text-sm text-mutedGray">Loading recipients…</p> : null}
              </div>
            </section>
          </div>

          <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
            <section className="panel overflow-hidden">
              <div className="border-b border-gray-200 p-4 dark:border-darkCard"><h3 className="font-semibold">Email preview</h3></div>
              <div className="bg-gray-100 p-4 dark:bg-black/20">
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-[#12202e] shadow-sm">
                  <div className="bg-[#11202d] px-5 py-4 font-semibold text-white">Pickaxe Trader</div>
                  <div className="p-5"><p className="mb-1 text-xs text-gray-500">{composer.previewText || 'Inbox preview appears here'}</p><h4 className="mb-5 text-lg font-bold">{composer.subject || 'Your campaign subject'}</h4><div className="whitespace-pre-wrap text-sm leading-6">{composer.bodyText || 'Generate an AI draft or write your message.'}</div>{composer.ctaLabel ? <span className="mt-5 inline-block rounded-md bg-[#2f7d32] px-4 py-2 text-sm font-bold text-white">{composer.ctaLabel}</span> : null}<p className="mt-6 text-xs text-gray-500">Risk notice and unsubscribe link are added automatically.</p></div>
                </div>
              </div>
            </section>

            <section className="panel overflow-hidden">
              <div className="border-b border-gray-200 p-4 dark:border-darkCard"><div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-indigo-500" /><h3 className="font-semibold">Delivery</h3></div></div>
              <div className="space-y-3 p-4">
                <label><span className="mb-1 block text-xs font-medium">Frequency</span><select className="input" value={scheduleType} onChange={(event) => setScheduleType(event.target.value as typeof scheduleType)}><option value="once">One time</option><option value="weekly">Every week</option><option value="monthly">Every month</option></select></label>
                <label><span className="mb-1 block text-xs font-medium">First scheduled delivery</span><input className="input" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} /></label>
                <p className="rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">Mail is released in small batches. This protects sender reputation and reduces provider rate-limit blocks.</p>
                <button className="btn-outline w-full" disabled={saveCampaign.isPending || !scheduledAt} onClick={() => submit('schedule')}><CalendarClock className="h-4 w-4" />Schedule campaign</button>
                <button className="btn w-full" disabled={saveCampaign.isPending} onClick={() => submit('send')}><Send className="h-4 w-4" />Queue safe send · {selected.size}</button>
              </div>
            </section>
          </div>
        </div>
      ) : (
        <section className="panel overflow-hidden">
          <div className="border-b border-gray-200 p-4 dark:border-darkCard"><h3 className="font-semibold">Campaign activity</h3><p className="mt-1 text-xs text-mutedGray dark:text-darkTextSecondary">Delivery counts update while the rate-limited worker sends each batch.</p></div>
          <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="bg-gray-50 text-left dark:bg-white/5"><tr><th className="px-4 py-3">Campaign</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Frequency</th><th className="px-3 py-3">Recipients</th><th className="px-3 py-3">Delivery</th><th className="px-3 py-3">Next run</th><th className="px-3 py-3">Action</th></tr></thead><tbody>{(campaigns.data?.items || []).map((campaign) => <tr key={campaign.id} className="border-t border-gray-100 align-top dark:border-darkCard"><td className="px-4 py-3"><strong className="block">{campaign.name}</strong><span className="text-xs text-mutedGray dark:text-darkTextSecondary">{campaign.subject}</span></td><td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold capitalize ${statusTone(campaign.status)}`}>{campaign.status}</span></td><td className="px-3 py-3 capitalize">{campaign.scheduleType}</td><td className="px-3 py-3 font-semibold">{campaign.recipientCount}</td><td className="px-3 py-3 text-xs"><span className="text-emerald-700 dark:text-emerald-300">{campaign.sentCount} sent</span><span className="block">{campaign.queuedCount} queued</span>{campaign.failedCount ? <span className="block text-red-600">{campaign.failedCount} failed</span> : null}{campaign.skippedCount ? <span className="block text-amber-700">{campaign.skippedCount} skipped</span> : null}</td><td className="px-3 py-3 text-xs">{dateTimeLabel(campaign.nextRunAt)}</td><td className="px-3 py-3">{['scheduled', 'active'].includes(campaign.status) ? <button className="inline-flex items-center gap-1 rounded border border-amber-300 px-2 py-1.5 text-xs text-amber-800 dark:text-amber-200" onClick={() => pauseCampaign.mutate({ id: campaign.id, action: 'pause' })}><Pause className="h-3.5 w-3.5" />Pause</button> : campaign.status === 'paused' && campaign.scheduleType !== 'once' ? <button className="inline-flex items-center gap-1 rounded border border-emerald-300 px-2 py-1.5 text-xs text-emerald-800 dark:text-emerald-200" onClick={() => pauseCampaign.mutate({ id: campaign.id, action: 'resume' })}><Play className="h-3.5 w-3.5" />Resume</button> : '—'}</td></tr>)}</tbody></table>{campaigns.isLoading ? <p className="p-5 text-sm text-mutedGray">Loading campaigns…</p> : null}{!campaigns.isLoading && !(campaigns.data?.items || []).length ? <p className="p-8 text-center text-sm text-mutedGray">No campaigns have been created.</p> : null}</div>
        </section>
      )}
    </div>
  );
}

